<template>
  <profile-edit-section-card
    v-if="localState"
    title="Rate"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <fieldset class="mb-24">
      <legend class="form__label pb-8">Set the rates for the services you provide</legend>
      <ul>
        <li v-for="(entry, index) in localState.serviceRates" :key="entry.service" class="mb-8">
          <poppins-checkbox
            :checkbox-id="`${entry.service}-selected`"
            :model-value="entry.selected"
            :label-name="rateOptionLabel(entry.service)"
            @update:model-value="onServiceToggle(index, $event)"
          />
          <poppins-text-field
            v-if="entry.selected"
            :id="`${entry.service}-rate`"
            :model-value="entry.rate"
            class="mt-8 mb-16"
            :label-name="rateOptionInputLabel(entry.service)"
            type="text"
            pattern="[0-9]*"
            maxlength="4"
            inputmode="numeric"
            :required="true"
            :status="entry.status"
            :helper-text="entry.status === HelperStatus.Error ? 'Must add rate before saving' : ''"
            :limit-input-to-pattern="true"
            @update:model-value="onRateInput(index, $event)"
          />
        </li>
      </ul>
    </fieldset>
    <poppins-dropdown
      id="distance-willing-to-travel"
      v-model="localState.distanceWillingToTravel"
      label-name="How many miles from home are you willing to work?"
      :options="distanceOptions"
    />
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsTextField from '@/components/textfield/TextField.vue';
import PoppinsDropdown from '@/components/dropdown/Dropdown.vue';
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import { COMPANION_CARE_SERVICE_RATE_OPTIONS, distanceOptions } from '../../__types__/CareTypeFormOptions';
import type { CompanionCareProfileFormState, CompanionCareServiceRateFormEntry } from '../../__types__/CompanionCareProfileTypes';
import { ratesToServiceRateFormEntries, serviceRateFormEntriesToUpsert } from '../../lib/companionCareRateUtils';
import HelperStatus from '@/helpers/status';

type RateFormEntry = CompanionCareServiceRateFormEntry & {
  status: HelperStatus;
};

const isMissingRate = (entry: RateFormEntry): boolean =>
  entry.selected && (!entry.rate || Number.isNaN(Number(entry.rate)) || Number(entry.rate) <= 0);

export default {
  name: 'CompanionCareRateForm',
  components: {
    ProfileEditSectionCard,
    PoppinsTextField,
    PoppinsDropdown,
    PoppinsCheckbox,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
      HelperStatus,
    };
  },
  computed: {
    distanceOptions: () => distanceOptions(),
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      const current = {
        serviceRates: this.localState.serviceRates.map(({ service, selected, rate }) => ({ service, selected, rate })),
        distanceWillingToTravel: this.localState.distanceWillingToTravel,
      };
      const original = {
        serviceRates: this.originalState.serviceRates.map(({ service, selected, rate }) => ({ service, selected, rate })),
        distanceWillingToTravel: this.originalState.distanceWillingToTravel,
      };
      return JSON.stringify(current) !== JSON.stringify(original);
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    rateOptionLabel(service: string): string {
      return COMPANION_CARE_SERVICE_RATE_OPTIONS.find((option) => option.service === service)?.checkboxLabel || service;
    },
    rateOptionInputLabel(service: string): string {
      return COMPANION_CARE_SERVICE_RATE_OPTIONS.find((option) => option.service === service)?.inputLabel || '$ Rate';
    },
    onServiceToggle(index: number, selected: boolean) {
      if (!this.localState?.serviceRates?.[index]) return;
      this.localState.serviceRates[index].selected = selected;
      if (!selected) {
        this.localState.serviceRates[index].rate = '';
        this.localState.serviceRates[index].status = HelperStatus.Neutral;
      }
    },
    onRateInput(index: number, value: string) {
      if (!this.localState?.serviceRates?.[index]) return;
      this.localState.serviceRates[index].rate = value;
      // Only clear an existing save error once the rate is valid; don't show errors while typing
      if (
        this.localState.serviceRates[index].status === HelperStatus.Error &&
        !isMissingRate(this.localState.serviceRates[index])
      ) {
        this.localState.serviceRates[index].status = HelperStatus.Neutral;
      }
    },
    initializeFormState() {
      const state = this.formState as CompanionCareProfileFormState | null;
      if (!state) return;

      this.localState = {
        serviceRates: ratesToServiceRateFormEntries(state.rates).map((entry) => ({
          ...entry,
          status: HelperStatus.Neutral,
        })),
        distanceWillingToTravel: String(state.distanceWillingToTravel || 10),
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    async handleSave() {
      if (!this.localState) return;

      this.localState.serviceRates = this.localState.serviceRates.map((entry) => ({
        ...entry,
        status: isMissingRate(entry) ? HelperStatus.Error : HelperStatus.Neutral,
      }));

      const firstInvalid = this.localState.serviceRates.find(isMissingRate);
      if (firstInvalid) {
        this.$nextTick(() => {
          const field = document.getElementById(`${firstInvalid.service}-rate`);
          field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          field?.focus({ preventScroll: true });
        });
        return;
      }
      if (!this.hasUnsavedChanges) return;

      await this.saveSection({
        serviceRates: serviceRateFormEntriesToUpsert(this.localState.serviceRates),
        distanceWillingToTravel: Number(this.localState.distanceWillingToTravel),
      });
    },
  },
};
</script>
